import { supabase } from './supabase'
import type { Task } from './types'

/**
 * The reads/writes side of tasks — shared between the per-agent checklist on
 * `AgentPanel.tsx` and the cross-agent Tasks tab (`routes/Tasks.tsx`), so
 * "what a task write actually does" has one answer instead of two that can
 * drift. The pure sort/date logic lives separately, in `tasksort.ts` — this
 * file imports `supabase`, which makes it untestable under the plain-Node
 * harness the same way `outbox.ts`/`sender.ts` already are; keeping that
 * boundary in its own file is what lets `tasksort.ts` stay tested.
 */

export async function createTask(
  agentId: string,
  body: string,
  dueDate: string | null,
  createdBy: string | null,
): Promise<{ task: Task | null; error: string | null }> {
  const trimmed = body.trim()
  if (!trimmed) return { task: null, error: 'A task needs words.' }
  const { data, error } = await supabase
    .from('tasks')
    .insert({ agent_id: agentId, body: trimmed, due_date: dueDate, created_by: createdBy })
    .select('*')
    .single()
  if (error) return { task: null, error: error.message }
  return { task: data as Task, error: null }
}

/**
 * Flips `done` and sets/clears `done_at` in the same write — never a
 * separate call, so the two can't land out of step if this one fails
 * partway (there is no partway: it's one `update`).
 */
export async function toggleTaskDone(task: Task): Promise<{ task: Task | null; error: string | null }> {
  const nextDone = !task.done
  const { data, error } = await supabase
    .from('tasks')
    .update({ done: nextDone, done_at: nextDone ? new Date().toISOString() : null })
    .eq('id', task.id)
    .select('*')
    .single()
  if (error) return { task: null, error: error.message }
  return { task: data as Task, error: null }
}

export async function deleteTask(id: number): Promise<string | null> {
  const { error } = await supabase.from('tasks').delete().eq('id', id)
  return error?.message ?? null
}
